import { initializeApp } from 'firebase/app'
import { deleteToken, getMessaging, getToken, onMessage } from 'firebase/messaging'
import { get, set, del, keys } from 'idb-keyval'
import { firebaseConfig } from '../config'
import { ClassLogger } from '../lib/logging/class_logger'
import { DeviceDetection } from '../lib/device_detection'
import { showToast } from './toast'
import { UserActionsLogger } from '../lib/user_actions_logger'
import { notifications, notification } from '../routes'

const logger = new ClassLogger('FirebaseNotificationManager')
const log = logger.log.bind(logger)
const error = logger.error.bind(logger)

export class FirebaseNotificationManager {
  static messaging = null
  static serviceWorkerRegistration = null
  static foregroundHandlerRegistered = false

  static getStorageKey (ecosystemId) {
    if (!ecosystemId) {
      throw new Error('Ecosystem ID is required for token storage')
    }
    return `firebase_subscription_${ecosystemId}`
  }

  static isSupported () {
    if (!DeviceDetection.isSecureContext()) {
      return 'Requires HTTPS or localhost'
    }
    if (!DeviceDetection.hasServiceWorkerSupport()) {
      return 'Service Workers not supported'
    }
    if (!DeviceDetection.hasNotificationSupport()) {
      return 'Notifications not supported'
    }
    if (firebaseConfig.apiKey === '') {
      return 'Firebase not configured'
    }
    if (DeviceDetection.isIOSPWA()) {
      return 'Use native notifications in iOS PWA'
    }
    return true
  }

  static async migrateFromLocalStorage () {
    try {
      // Clean up legacy localStorage token
      const localStorageToken = window.localStorage.getItem('firebase_subscription')
      if (localStorageToken) {
        log('migrateFromLocalStorage() - Found legacy token in localStorage, removing (ecosystem-scoped tokens required)')
        window.localStorage.removeItem('firebase_subscription')
      }

      // Clean up legacy IndexedDB token (non-ecosystem-scoped)
      const oldIndexedDBToken = await get('firebase_subscription')
      if (oldIndexedDBToken) {
        log('migrateFromLocalStorage() - Found legacy token in IndexedDB, removing (ecosystem-scoped tokens required)')
        await del('firebase_subscription')
      }

      if (localStorageToken || oldIndexedDBToken) {
        log('migrateFromLocalStorage() - Legacy tokens removed. Users will need to re-subscribe per ecosystem.')
      }
    } catch (err) {
      error('migrateFromLocalStorage() - Error during migration:', err)
    }
  }

  static async ensureServiceWorkerRegistered () {
    if (this.serviceWorkerRegistration) {
      log('ensureServiceWorkerRegistered() - Already have registration, returning cached')
      return this.serviceWorkerRegistration
    }
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported')
    }
    try {
      // Clean up old service worker if it exists
      await this.cleanupOldServiceWorker()

      log('ensureServiceWorkerRegistered() - Checking for existing service worker...')
      const existingRegistration = await navigator.serviceWorker.getRegistration('/')
      if (existingRegistration) {
        log('ensureServiceWorkerRegistered() - Found existing registration:', existingRegistration)
        log('ensureServiceWorkerRegistered() - Active worker:', existingRegistration.active)
        log('ensureServiceWorkerRegistered() - Installing worker:', existingRegistration.installing)
        log('ensureServiceWorkerRegistered() - Waiting worker:', existingRegistration.waiting)
        this.serviceWorkerRegistration = existingRegistration
        log('ensureServiceWorkerRegistered() - Using existing registration')
        return existingRegistration
      }

      log('ensureServiceWorkerRegistered() - No existing registration, registering new service worker...')

      // Add timeout to prevent hanging forever
      const timeoutPromise = new Promise((_resolve, reject) => {
        setTimeout(() => reject(new Error('Service worker registration timed out after 10 seconds')), 10000)
      })

      const registrationPromise = navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      )

      this.serviceWorkerRegistration = await Promise.race([registrationPromise, timeoutPromise])
      log('ensureServiceWorkerRegistered() - Service worker registered:', this.serviceWorkerRegistration)
      log('ensureServiceWorkerRegistered() - Waiting for service worker to be ready...')

      const readyPromise = navigator.serviceWorker.ready
      await Promise.race([readyPromise, timeoutPromise])

      log('ensureServiceWorkerRegistered() - Service worker is ready!')
      return this.serviceWorkerRegistration
    } catch (err) {
      error('ensureServiceWorkerRegistered() - Failed to register Firebase service worker:', err)
      throw err
    }
  }

  static async cleanupOldServiceWorker () {
    try {
      // Get all service worker registrations
      const registrations = await navigator.serviceWorker.getRegistrations()

      for (const registration of registrations) {
        if (!registration.active) continue

        const scriptURL = registration.active.scriptURL

        // Check if this is the old /service-worker.js
        if (scriptURL.includes('/service-worker.js')) {
          log('cleanupOldServiceWorker() - Found old service worker, unregistering:', scriptURL)
          await registration.unregister()
          log('cleanupOldServiceWorker() - Old service worker unregistered successfully')
        }

        // Check if this is Firebase's auto-registered worker at the firebase-cloud-messaging-push-scope
        if (scriptURL.includes('firebase-cloud-messaging-push-scope')) {
          log('cleanupOldServiceWorker() - Found Firebase auto-registered worker, unregistering:', scriptURL)
          await registration.unregister()
          log('cleanupOldServiceWorker() - Firebase auto-registered worker unregistered successfully')
        }
      }
    } catch (err) {
      error('cleanupOldServiceWorker() - Error cleaning up old service worker:', err)
      // Don't throw - this is best effort cleanup
    }
  }

  static async initialize () {
    const supported = this.isSupported()
    if (supported !== true) {
      throw new Error(`Firebase notifications not supported: ${supported}`)
    }
    await this.ensureServiceWorkerRegistered()
    if (!this.messaging) {
      initializeApp(firebaseConfig)
      this.messaging = getMessaging()

      // Register foreground message handler once
      this.registerForegroundHandler()
    }
    return this.messaging
  }

  static registerForegroundHandler () {
    if (this.foregroundHandlerRegistered) {
      log('Foreground handler already registered')
      return
    }

    log('Registering foreground message handler...')
    onMessage(this.messaging, (payload) => {
      log('Foreground message received:', payload)
      try {
        // Parse data-only message format
        if (payload.data && payload.data.notification) {
          const notification = JSON.parse(payload.data.notification)
          log('Parsed notification:', notification)
          const options = {}
          if (notification.click_action) {
            options.url = notification.click_action
            log('Notification has click action:', notification.click_action)
          }
          showToast(notification.title, notification.body, options)
        } else if (payload.notification) {
          // Fallback for old notification format
          log('Using legacy notification format')
          showToast(payload.notification.title, payload.notification.body)
        } else {
          log('Unknown payload format:', payload)
        }
      } catch (err) {
        error('Error handling foreground message:', err)
      }
    })

    this.foregroundHandlerRegistered = true
    log('Foreground handler registered successfully')
  }

  static async getToken () {
    try {
      const supported = this.isSupported()
      if (supported !== true) {
        log(`Firebase not supported: ${supported}`)
        return null
      }
      if (window.Notification.permission !== 'granted') {
        return null
      }
      const messaging = await this.initialize()
      const firebaseToken = await getToken(messaging, {
        vapidKey: firebaseConfig.vapidKey,
        serviceWorkerRegistration: this.serviceWorkerRegistration
      })
      return firebaseToken
    } catch (err) {
      error('Error getting Firebase token:', err)
      return null
    }
  }

  static async hasToken () {
    const token = await this.getToken()
    return !!token
  }

  static async getTokenForEcosystem (ecosystemId) {
    try {
      const stored = await get(this.getStorageKey(ecosystemId))
      // Handle both old format (string) and new format (object with token + id)
      if (typeof stored === 'string') {
        return stored
      } else if (stored && stored.token) {
        return stored.token
      }
      return null
    } catch (err) {
      error('getTokenForEcosystem() - Error:', err)
      return null
    }
  }

  static async hasTokenForEcosystem (ecosystemId) {
    const token = await this.getTokenForEcosystem(ecosystemId)
    return !!token
  }

  static async getAllEcosystemSubscriptions () {
    try {
      const allKeys = await keys()
      const subscriptionKeys = allKeys.filter(k => k.startsWith('firebase_subscription_'))
      const result = []

      for (const key of subscriptionKeys) {
        const ecosystemId = key.replace('firebase_subscription_', '')
        const token = await get(key)
        if (token) {
          result.push({ ecosystemId, token, key })
        }
      }

      log(`getAllEcosystemSubscriptions() - Found ${result.length} ecosystem subscription(s)`)
      return result
    } catch (err) {
      error('getAllEcosystemSubscriptions() - Error:', err)
      return []
    }
  }

  static async subscribe (ecosystemId) {
    log('subscribe() - Starting subscription flow')
    log('subscribe() - Current permission status:', window.Notification.permission)

    if (window.Notification.permission === 'denied') {
      error('subscribe() - Notifications are blocked by user')
      throw new Error('Notifications are blocked')
    }

    log('subscribe() - Initializing Firebase messaging...')
    const messaging = await this.initialize()
    log('subscribe() - Firebase messaging initialized')

    log('subscribe() - Requesting notification permission and getting token...')
    log('subscribe() - This may show a permission prompt to the user')

    const firebaseToken = await getToken(messaging, {
      vapidKey: firebaseConfig.vapidKey,
      serviceWorkerRegistration: this.serviceWorkerRegistration
    })

    log('subscribe() - getToken() completed')
    log('subscribe() - Token received:', firebaseToken ? 'Yes (length: ' + firebaseToken.length + ')' : 'No')
    log('subscribe() - Permission after getToken():', window.Notification.permission)

    if (!firebaseToken) {
      error('subscribe() - No Firebase registration token available')
      throw new Error('No Firebase registration token available')
    }

    log('subscribe() - Registering token with server...')
    const response = await fetch(notifications(), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        token: firebaseToken,
        token_type: 'firebase',
        ecosystem_id: ecosystemId
      })
    })

    log('subscribe() - Server response status:', response.status)

    if (!response.ok) {
      error('subscribe() - Failed to register token with server, status:', response.status)
      throw new Error('Failed to register token with server')
    }

    const data = await response.json()
    log('subscribe() - Server response:', data)
    log('subscribe() - Subscription completed successfully!')

    try {
      // Store both token and id for future verification
      await set(this.getStorageKey(ecosystemId), {
        token: data.token,
        id: data.id
      })
    } catch (err) {
      error('subscribe() - Error storing token in IndexedDB:', err)
      throw new Error('Failed to store subscription token')
    }

    return { token: firebaseToken, message: data.message || 'Successfully subscribed' }
  }

  static async unsubscribeFromPushManager () {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (registration) {
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await subscription.unsubscribe()
          log('Unsubscribed from PushManager successfully')
        }
      }
    } catch (err) {
      error('Error unsubscribing from PushManager:', err)
      throw err
    }
  }

  static async deleteTokenFromBrowser () {
    try {
      const messaging = await this.initialize()
      await deleteToken(messaging)
      log('Firebase token deleted from browser IndexedDB')
    } catch (err) {
      error('Error deleting Firebase token from browser:', err)
      throw err
    }
  }

  static async unsubscribe (ecosystemId) {
    // Attempt migration from localStorage if needed
    await this.migrateFromLocalStorage()

    let storedData
    try {
      storedData = await get(this.getStorageKey(ecosystemId))
    } catch (err) {
      error('unsubscribe() - Error retrieving token from IndexedDB:', err)
      throw new Error('Failed to retrieve subscription token')
    }

    if (!storedData) {
      throw new Error('No active subscription found')
    }

    // Handle both old format (string) and new format (object with id)
    let tokenId
    if (typeof storedData === 'string') {
      // Old format - no ID, can't use ID-based deletion
      tokenId = null
    } else if (storedData.id) {
      // New format - has ID, prefer ID-based deletion
      tokenId = storedData.id
    }

    let response
    if (tokenId) {
      // Use ID-based deletion (more reliable)
      log('unsubscribe() - Using ID-based deletion, tokenId:', tokenId)
      response = await fetch(notification(tokenId), {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      })
    } else {
      // Fall back to token-based deletion (legacy)
      const tokenString = typeof storedData === 'string' ? storedData : storedData.token
      log('unsubscribe() - Using legacy token-based deletion')
      response = await fetch(
        notifications({
          token: tokenString,
          token_type: 'firebase',
          ecosystem_id: ecosystemId
        }),
        {
          method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      })
    }

    if (!response.ok) {
      throw new Error('Failed to delete token')
    }

    try {
      await del(this.getStorageKey(ecosystemId))
    } catch (err) {
      error('unsubscribe() - Error deleting token from IndexedDB:', err)
    }

    // Check if any other ecosystems still have subscriptions
    const allKeys = await keys()
    const remainingFirebaseKeys = allKeys.filter(k =>
      k.startsWith('firebase_subscription_') && k !== this.getStorageKey(ecosystemId)
    )

    if (remainingFirebaseKeys.length === 0) {
      // No other ecosystems subscribed - safe to delete browser's Firebase token
      log('unsubscribe() - No other ecosystem subscriptions, cleaning browser Firebase token')
      await this.unsubscribeFromPushManager()
      await this.deleteTokenFromBrowser()
    } else {
      log(`unsubscribe() - ${remainingFirebaseKeys.length} other ecosystem subscription(s) exist, preserving browser Firebase token`)
    }

    const body = await response.text()
    return body
  }

  static async unsubscribeAllTokensForEcosystem (ecosystemId) {
    // Delete ALL Firebase tokens for this ecosystem from server
    const response = await fetch(
      notifications({
        token_type: 'firebase',
        ecosystem_id: ecosystemId
      }),
      {
        method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })

    if (!response.ok) {
      throw new Error('Failed to delete all Firebase tokens for ecosystem')
    }

    // Delete local storage for this ecosystem
    try {
      await del(this.getStorageKey(ecosystemId))
    } catch (err) {
      error('unsubscribeAllTokensForEcosystem() - Error deleting token from IndexedDB:', err)
    }

    // Only delete browser Firebase token if no other ecosystems subscribed
    const allSubs = await this.getAllEcosystemSubscriptions()
    if (allSubs.length === 0) {
      log('unsubscribeAllTokensForEcosystem() - No other ecosystem subscriptions, cleaning browser Firebase token')
      await this.unsubscribeFromPushManager()
      await this.deleteTokenFromBrowser()
    } else {
      log(`unsubscribeAllTokensForEcosystem() - ${allSubs.length} other ecosystem subscription(s) exist, preserving browser Firebase token`)
    }

    const data = await response.json()
    return data.message || 'Successfully unsubscribed'
  }

  static async unsubscribeAllEcosystems () {
    const allSubs = await this.getAllEcosystemSubscriptions()

    for (const { ecosystemId } of allSubs) {
      try {
        await this.unsubscribe(ecosystemId)
        log(`unsubscribeAllEcosystems() - Unsubscribed from ecosystem ${ecosystemId}`)
      } catch (err) {
        error(`unsubscribeAllEcosystems() - Error unsubscribing from ecosystem ${ecosystemId}:`, err)
      }
    }

    log('unsubscribeAllEcosystems() - Unsubscribed from all ecosystems')
  }

  /**
   * Synchronize Firebase token verification with backend
   *
   * This method updates the last_verified_at timestamp on the backend
   * Firebase tokens are stable (don't rotate like Apple endpoints)
   * so we only need to update verification timestamps, not token values
   *
   * @param {string} ecosystemId - Ecosystem UUID
   * @returns {Promise<Object>} Result object with sync details
   * @returns {boolean} result.synced - Whether sync completed successfully
   * @returns {boolean} result.orphanCleaned - Whether orphaned token was removed
   */
  static async syncToken (ecosystemId) {
    console.log('🔄 [Firebase] Starting syncToken() for ecosystem:', ecosystemId)

    try {
      // PHASE 1: Get stored token from IndexedDB
      const storedData = await get(this.getStorageKey(ecosystemId))
      console.log('🔍 [Firebase] Stored data:', storedData ? 'present' : 'none')

      if (!storedData) {
        console.log('✅ [Firebase] No stored token for this ecosystem')
        return { synced: true, orphanCleaned: false }
      }

      // Handle old format (string token) vs new format (object with id)
      let tokenString, tokenId

      if (typeof storedData === 'string') {
        // Old format: just the token string
        tokenString = storedData
        tokenId = null
        console.log('🔍 [Firebase] Old format detected (string token, no ID)')
      } else if (storedData.token && storedData.id) {
        // New format: object with token and id
        tokenString = storedData.token
        tokenId = storedData.id
        console.log('🔍 [Firebase] New format detected (token + ID)')
      } else {
        console.log('❌ [Firebase] Invalid stored data format')
        return { synced: false, orphanCleaned: false }
      }

      // PHASE 2: Get current browser token
      const browserToken = await this.getToken()
      console.log('🔍 [Firebase] Browser token:', browserToken ? 'present' : 'none')

      if (!browserToken) {
        console.log('🧹 [Firebase] Browser token missing, cleaning IndexedDB')
        await del(this.getStorageKey(ecosystemId))

        // Log orphan cleanup
        await UserActionsLogger.logFirebase('Orphan token cleaned (browser revoked)', {
          reason: 'browser_missing',
          ecosystemId,
          tokenId: tokenId || 'unknown'
        })

        return { synced: true, orphanCleaned: true }
      }

      // PHASE 3: Verify stored token matches browser token
      if (tokenString !== browserToken) {
        console.log('⚠️ [Firebase] Token mismatch! Stored vs Browser different')
        console.log('🔍 [Firebase] Stored:', tokenString.substring(0, 20) + '...')
        console.log('🔍 [Firebase] Browser:', browserToken.substring(0, 20) + '...')

        // Log the mismatch - this shouldn't happen with Firebase
        await UserActionsLogger.logFirebase('Token mismatch detected ‼️‼️', {
          ecosystemId,
          tokenId: tokenId || 'unknown',
          storedPreview: tokenString.substring(0, 20),
          browserPreview: browserToken.substring(0, 20)
        })

        // Re-subscribe with the browser's current token
        console.log('🔄 [Firebase] Re-subscribing with browser token')
        await this.subscribe(ecosystemId)
        return { synced: true, orphanCleaned: false }
      }

      // PHASE 4: If we don't have tokenId, look it up via backend
      if (!tokenId) {
        console.log('🔍 [Firebase] No token ID stored, checking backend for token existence')
        const exists = await this.anyFirebaseTokenExistsOnBackend(ecosystemId)

        if (!exists) {
          console.log('🧹 [Firebase] Token not found on backend, cleaning IndexedDB')
          await del(this.getStorageKey(ecosystemId))

          // Log orphan cleanup
          await UserActionsLogger.logFirebase('Orphan token cleaned (backend missing)', {
            reason: 'backend_missing_no_token_id',
            ecosystemId,
            tokenPreview: tokenString.substring(0, 20)
          })

          return { synced: true, orphanCleaned: true }
        }

        // Token exists but we don't have ID - need to re-subscribe to get it
        console.log('🔄 [Firebase] Token exists but no ID - re-subscribing to get ID')
        await this.subscribe(ecosystemId)
        return { synced: true, orphanCleaned: false }
      }

      // PHASE 5: PATCH backend to update last_verified_at
      console.log('🔍 [Firebase] PATCHing backend to update verification timestamp')
      console.log('🔍 [Firebase] Token ID:', tokenId)

      const response = await fetch(notification(tokenId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({
          token_type: 'firebase'
        })
      })

      console.log('🔍 [Firebase] Backend response status:', response.status)

      // Handle orphan cases (404 = deleted, 403 = wrong user)
      if (response.status === 404) {
        console.log('🔄 [Firebase] Token not found (404), attempting to recreate on backend')

        // Get stored token string from IndexedDB
        const storedData = await get(this.getStorageKey(ecosystemId))
        const tokenString = typeof storedData === 'string' ? storedData : storedData?.token

        if (!tokenString) {
          console.log('🧹 [Firebase] No token available for recreation, cleaning up')
          await del(this.getStorageKey(ecosystemId))
          await UserActionsLogger.logFirebase('Orphan token cleaned (no recovery data)', {
            reason: 'backend_404_no_token',
            ecosystemId,
            tokenId
          })
          return { synced: true, orphanCleaned: true }
        }

        // Attempt to recreate token on backend
        try {
          console.log('🔄 [Firebase] Recreating token on backend...')
          const recreateResponse = await fetch(notifications(), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
            },
            body: JSON.stringify({
              token: tokenString,
              token_type: 'firebase',
              ecosystem_id: ecosystemId
            })
          })

          if (recreateResponse.ok) {
            const data = await recreateResponse.json()
            console.log('✅ [Firebase] Token successfully recreated:', data.token_id)

            // Update IndexedDB with new token ID
            await set(this.getStorageKey(ecosystemId), {
              token: tokenString,
              id: data.token_id
            })

            // Log successful adoption
            await UserActionsLogger.logFirebase('Orphan subscription adopted 🏠', {
              reason: 'backend_404_adopted',
              ecosystemId,
              oldTokenId: tokenId,
              newTokenId: data.token_id,
              tokenPreview: tokenString.substring(0, 20)
            })

            return { synced: true, orphanCleaned: false, adopted: true }
          } else {
            console.error('❌ [Firebase] Failed to recreate token:', recreateResponse.status)
            throw new Error(`Recreation failed with status ${recreateResponse.status}`)
          }
        } catch (err) {
          console.error('❌ [Firebase] Error recreating token:', err)
          // Fall back to cleanup
          await del(this.getStorageKey(ecosystemId))
          await UserActionsLogger.logFirebase('Orphan token cleaned (recreation failed)', {
            reason: 'backend_404_recreation_failed',
            ecosystemId,
            tokenId,
            error: err.message
          })
          return { synced: true, orphanCleaned: true }
        }
      }

      if (response.status === 403) {
        console.log('🧹 [Firebase] Access denied (403), cleaning up')
        await del(this.getStorageKey(ecosystemId))

        // Log orphan cleanup
        await UserActionsLogger.logFirebase('Orphan token cleaned (wrong user)', {
          reason: 'backend_403_access_denied',
          ecosystemId,
          tokenId
        })

        return { synced: true, orphanCleaned: true }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ [Firebase] Backend PATCH failed:', response.status, errorData)
        return { synced: false, orphanCleaned: false }
      }

      // PHASE 6: Parse backend response
      const data = await response.json()
      console.log('✅ [Firebase] Backend response:', data)
      console.log('✅ [Firebase] Token verified:', data.message)

      // PHASE 7: Return result
      const result = {
        synced: true,
        orphanCleaned: false
      }
      console.log('✅ [Firebase] Sync complete:', result)
      return result
    } catch (err) {
      console.error('❌ [Firebase] Error in syncToken():', err)

      // Log the error
      await UserActionsLogger.logFirebase('Sync error', {
        ecosystemId,
        error: err.message,
        errorType: err.name
      }).catch(() => {
        // Silent fail if logging fails - don't block the sync operation
      })

      return { synced: false, orphanCleaned: false }
    }
  }

  static async verifyAndCleanOrphan (ecosystemId) {
    console.log('🔍 [Firebase] Starting verifyAndCleanOrphan() for ecosystem:', ecosystemId)

    try {
      // Check if this specific ecosystem has Firebase tokens on backend
      console.log('🔍 [Firebase] Checking if ecosystem has Firebase tokens on backend...')
      const ecosystemHasTokens = await this.anyFirebaseTokenExistsOnBackend(ecosystemId)
      console.log('🔍 [Firebase] Backend tokens exist for ecosystem:', ecosystemHasTokens)

      if (!ecosystemHasTokens) {
        // This ecosystem has no backend tokens, check if browser has one
        const localToken = await this.getTokenForEcosystem(ecosystemId)
        console.log('🔍 [Firebase] Local token for ecosystem:', localToken ? `present (${localToken.substring(0, 20)}...)` : 'none')

        if (localToken) {
          console.log('🔍 [Firebase] Ecosystem has local token but no backend token - cleaning local token')
          await del(this.getStorageKey(ecosystemId))

          // Check if ANY other ecosystems still have subscriptions
          const allSubs = await this.getAllEcosystemSubscriptions()
          if (allSubs.length === 0) {
            console.log('🔍 [Firebase] No other ecosystem subscriptions exist - cleaning browser Firebase token')
            await this.unsubscribeFromPushManager()
            await this.deleteTokenFromBrowser()
          } else {
            console.log(`🔍 [Firebase] ${allSubs.length} other ecosystem subscription(s) exist - preserving browser Firebase token`)
          }

          console.log('🔍 [Firebase] Orphaned token cleaned, returning true')
          return true // Cleaned orphan
        }
      }

      console.log('🔍 [Firebase] No orphan found, returning false')
      return false
    } catch (err) {
      console.error('🔍 [Firebase] Error in verifyAndCleanOrphan():', err)
      return false
    }
  }

  static async anyFirebaseTokenExistsOnBackend (ecosystemId) {
    console.log('🔍 [Firebase] Checking any Firebase token existence on backend...')
    console.log('🔍 [Firebase] Ecosystem ID:', ecosystemId)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
      console.log('🔍 [Firebase] CSRF token present:', !!csrfToken)

      const url = `/notifications/check_token?token=any&type=firebase&ecosystem_id=${ecosystemId}`
      console.log('🔍 [Firebase] Backend check URL:', url)

      const response = await fetch(url, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })

      console.log('🔍 [Firebase] Backend response status:', response.status)

      if (!response.ok) {
        console.log('🔍 [Firebase] Backend response not ok, returning false')
        return false
      }

      const data = await response.json()
      console.log('🔍 [Firebase] Backend response data:', data)

      const exists = data.exists
      console.log('🔍 [Firebase] Final any exists result:', exists)
      return exists
    } catch (err) {
      console.error('🔍 [Firebase] Error checking any Firebase token:', err)
      return false
    }
  }

  static async tokenExistsOnBackend (token) {
    console.log('🔍 [Firebase] Checking token existence on backend...')
    console.log('🔍 [Firebase] Token (first 50 chars):', token ? token.substring(0, 50) : 'null')

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
      console.log('🔍 [Firebase] CSRF token present:', !!csrfToken)

      const url = `/notifications/check_token?token=${encodeURIComponent(token)}&type=firebase`
      console.log('🔍 [Firebase] Backend check URL:', url)

      const response = await fetch(url, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })

      console.log('🔍 [Firebase] Backend response status:', response.status)
      console.log('🔍 [Firebase] Backend response ok:', response.ok)

      if (!response.ok) {
        console.log('🔍 [Firebase] Backend response not ok, returning false')
        return false
      }

      const data = await response.json()
      console.log('🔍 [Firebase] Backend response data:', data)

      const exists = data.exists
      console.log('🔍 [Firebase] Final exists result:', exists)
      return exists
    } catch (err) {
      console.error('🔍 [Firebase] Error checking backend token:', err)
      return false
    }
  }

  static getPermissionStatus () {
    if (!window.Notification) {
      return 'unsupported'
    }
    return window.Notification.permission
  }
}
